import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BookOpen, Camera, Bell, Info, Lock, Pencil } from 'lucide-react';
import HubLayout from '../components/common/HubLayout';
import {
  getMemberForEdit,
  updateMemberProfile,
  removeMemberFromFamily,
  getMemberHistory,
  uploadProfilePhoto,
} from '../services/api';

const C = {
  ink: '#2C2F26',
  soft: '#5A6150',
  muted: '#828A76',
  label: '#4A5040',
  primary: '#556B2F',
  primaryDark: '#3E4F22',
  cardBorder: '#E3E0D0',
  inputBorder: '#DCD8C8',
  editBorder: '#D4D0C0',
  rowBorder: '#F0EDE2',
  emptyText: '#9A9C8C',
  savedText: '#2E7A3A',
  lockedBg: '#F3F2EA',
  noticeBg: '#EEF1E4',
  noticeBorder: '#DCE3CB',
  danger: '#B23A1E',
  dangerBorder: '#E2B8A8',
  dangerBg: '#FFFBF8',
};
const STATUS_COLORS = { active: '#2E9E4F', guardian: '#3B6FD4', invite: '#E65100', tree: '#8A6FB0' };
const BODY_FONT = "'Source Sans 3', 'Source Sans Pro', system-ui, sans-serif";
const HEAD_FONT = "'Lora', Georgia, serif";

// Each card saves on its own. Field lists are unchanged from the original page.
const CARDS = [
  { key: 'personal', title: 'Personal information', fields: ['first_name', 'last_name', 'nickname', 'date_of_birth'] },
  { key: 'contact', title: 'Location & contact', fields: ['location', 'email', 'phone'] },
  { key: 'family', title: 'Family', fields: ['relationship', 'role'] },
  { key: 'privacy', title: 'Privacy', fields: ['privacy_default'] },
];

const FIELD_LABELS = {
  first_name: 'First name',
  last_name: 'Last name',
  nickname: 'Nickname',
  date_of_birth: 'Date of birth',
  location: 'Location',
  email: 'Email address',
  phone: 'Phone',
  relationship: 'Relationship to you',
  role: 'Role in this family',
  privacy_default: 'Default story privacy',
};
const ROLE_LABELS = { admin: 'Admin', member: 'Member', viewer: 'Viewer' };
const PRIVACY_LABELS = { family: 'Family only', private: 'Guardians only' };

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/api\/?$/, '');
const absUrl = (url) => (!url ? null : url.startsWith('http') ? url : `${API_ORIGIN}${url}`);

const norm = (v) => (v == null ? '' : String(v).trim());
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

function formatDob(iso) {
  if (!iso) return '';
  const [y, mo, d] = String(iso).split('-').map(Number);
  if (!y || !mo || !d) return iso;
  return new Date(y, mo - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTimestamp(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  if (Date.now() - d.getTime() < 60 * 1000) return 'Just now';
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${date} \u00B7 ${time}`;
}

const PAGE_CSS = `
  .efm-page { padding: 28px 32px 48px; }
  .efm-cols { display: flex; flex-wrap: wrap; gap: 28px; align-items: flex-start; }
  .efm-main { flex: 1 1 560px; max-width: 820px; min-width: 0; }
  .efm-aside { flex: 1 1 320px; max-width: 400px; min-width: 0; position: sticky; top: 20px; }
  .efm-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 18px 16px; }
  .efm-row { display: flex; justify-content: space-between; align-items: baseline; gap: 16px; padding: 12px 0; border-top: 1px solid ${C.rowBorder}; }
  .efm-row:first-child { border-top: none; }
  .efm-row-label { display: flex; align-items: center; gap: 6px; flex-shrink: 0; font-size: 15px; color: ${C.muted}; }
  .efm-row-value { min-width: 0; text-align: right; font-size: 16px; font-weight: 600; color: ${C.ink}; word-break: break-word; }
  .efm-sr-only { position: absolute; width: 1px; height: 1px; margin: -1px; padding: 0; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }
  .efm-saved { animation: efm-fade 3s ease forwards; }
  @keyframes efm-fade { 0% { opacity: 0; } 8% { opacity: 1; } 85% { opacity: 1; } 100% { opacity: 0; } }
  @media (prefers-reduced-motion: reduce) { .efm-saved { animation: none; } }
  @media (max-width: 639px) {
    .efm-page { padding: 16px 16px 40px; }
    .efm-cols { display: block; }
    .efm-main { max-width: none; }
    .efm-aside { max-width: none; position: static; margin-top: 8px; }
    .efm-grid { grid-template-columns: 1fr; }
  }
`;

function Avatar({ name, photo, size, bg = C.primary, fontSize }) {
  const url = absUrl(photo);
  const style = {
    width: size, height: size, borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
    background: bg, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: HEAD_FONT, fontWeight: 700, fontSize: fontSize || size * 0.42,
  };
  return (
    <div style={style}>
      {url ? (
        <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        (name || '?').charAt(0).toUpperCase()
      )}
    </div>
  );
}

function Field({ id, label, optional, locked, lockedHint, hint, error, children }) {
  return (
    <div>
      <label htmlFor={id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14.5, fontWeight: 600, color: C.label, marginBottom: 7 }}>
        {label}
        {optional && <span style={{ fontWeight: 400, color: C.muted }}>(optional)</span>}
        {locked && <Lock size={13} color="#9A9C8C" aria-label="Locked" />}
      </label>
      {children}
      {error ? (
        <p role="alert" style={{ fontSize: 14, color: C.danger, margin: '6px 0 0' }}>{error}</p>
      ) : locked ? (
        <p style={{ fontSize: 14, color: C.muted, margin: '6px 0 0' }}>{lockedHint}</p>
      ) : hint ? (
        <p style={{ fontSize: 14, color: C.muted, margin: '6px 0 0' }}>{hint}</p>
      ) : null}
    </div>
  );
}

const inputStyle = (error) => ({
  width: '100%', height: 46, boxSizing: 'border-box', padding: '0 14px', fontSize: 16, fontFamily: BODY_FONT,
  border: `1px solid ${error ? C.danger : C.inputBorder}`, borderRadius: 10,
  background: '#fff', color: C.ink, outline: 'none',
});

function HistoryEntry({ entry, firstName }) {
  const isChange = entry.action === 'changed' && entry.field;
  let verb = entry.action;
  let object = null;
  switch (entry.action) {
    case 'changed': verb = 'changed'; object = entry.field_label; break;
    case 'photo': verb = 'changed'; object = 'Photo'; break;
    case 'added': verb = 'added'; object = `${firstName} to the family`; break;
    case 'invited': verb = 'invited'; object = firstName; break;
    case 'resent': verb = 'resent the invite to'; object = firstName; break;
    case 'joined': verb = 'joined'; object = 'the family'; break;
    case 'removed': verb = 'removed'; object = `${firstName} from the family`; break;
    case 'restored': verb = 'restored'; object = `${firstName} to the family`; break;
    default: break;
  }
  const empty = <span style={{ fontStyle: 'italic' }}>Empty</span>;
  return (
    <li style={{ display: 'flex', gap: 12, padding: '12px 0', borderTop: `1px solid ${C.rowBorder}`, listStyle: 'none' }}>
      <Avatar name={entry.actor.name} photo={entry.actor.photo} size={32} fontSize={14} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 15, color: C.ink }}>
          <strong>{entry.actor.name}</strong> {verb} <strong>{object}</strong>
        </div>
        {isChange && (
          <div style={{ fontSize: 14.5, marginTop: 3, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, wordBreak: 'break-word' }}>
            <span style={{ color: '#9A9C8C', textDecoration: entry.old_value ? 'line-through' : 'none' }}>
              {entry.old_value || empty}
            </span>
            <ArrowRight size={13} color="#9A9C8C" />
            <span style={{ color: C.ink }}>{entry.new_value || empty}</span>
          </div>
        )}
        <div style={{ fontSize: 14, color: C.muted, marginTop: 3 }}>{formatTimestamp(entry.created_at)}</div>
      </div>
    </li>
  );
}

export default function EditFamilyMemberPage() {
  const { familyId, personId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [editing, setEditing] = useState(null); // card key currently in Edit mode
  const [draft, setDraft] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [cardError, setCardError] = useState(null);
  const [banner, setBanner] = useState(null);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null); // card key showing "Saved"
  const [liveMsg, setLiveMsg] = useState('');
  const [showRemove, setShowRemove] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [history, setHistory] = useState({ entries: [], next: null, total: 0, loading: true });

  const fileRef = useRef(null);
  const editBtnRefs = useRef({});
  const focusBackRef = useRef(null);
  const flashTimer = useRef(null);

  const loadHistory = useCallback(async (cursor) => {
    try {
      const res = await getMemberHistory(familyId, personId, cursor);
      setHistory((h) => ({
        entries: cursor ? [...h.entries, ...res.entries] : res.entries,
        next: res.next_cursor,
        total: res.total,
        loading: false,
      }));
    } catch (e) {
      setHistory((h) => ({ ...h, loading: false }));
    }
  }, [familyId, personId]);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setEditing(null);
    setLoadError(null);
    setHistory({ entries: [], next: null, total: 0, loading: true });
    (async () => {
      try {
        const payload = await getMemberForEdit(familyId, personId);
        if (cancelled) return;
        if (payload.is_self) {
          navigate('/settings', { replace: true });
          return;
        }
        setData(payload);
        loadHistory();
      } catch (e) {
        if (cancelled) return;
        const r = e.response;
        if (r && r.data && r.data.redirect) {
          navigate(r.data.redirect, { replace: true });
        } else if (r && r.status === 403) {
          navigate(`/life/${personId}`, { replace: true });
        } else if (r && r.status === 404) {
          setLoadError('We could not find this member in the family.');
        } else {
          setLoadError('Something went wrong loading this member. Please try again.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [familyId, personId, navigate, loadHistory]);

  useEffect(() => () => clearTimeout(flashTimer.current), []);

  // Focus: first input when a card opens; that card's Edit button when it closes.
  useEffect(() => {
    if (editing) {
      const root = document.getElementById(`card-${editing}`);
      const el = root && root.querySelector('input:not([readonly]), select, [role="radio"][aria-checked="true"]');
      if (el) el.focus();
    } else if (focusBackRef.current) {
      const btn = editBtnRefs.current[focusBackRef.current];
      focusBackRef.current = null;
      if (btn) btn.focus();
    }
  }, [editing]);

  const openDef = CARDS.find((c) => c.key === editing) || null;
  const dirty = !!(
    openDef && data &&
    openDef.fields.some((k) => data.editable_fields.includes(k) && norm(draft[k]) !== norm(data.member[k]))
  );

  // Warn on tab close / reload with unsaved edits in an open card.
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const go = (path) => {
    if (dirty && !window.confirm(`Discard your changes to ${openDef.title}?`)) return;
    navigate(path);
  };

  const showSaved = () => {
    clearTimeout(flashTimer.current);
    setLiveMsg('Saved');
    flashTimer.current = setTimeout(() => { setFlash(null); setLiveMsg(''); }, 3000);
  };

  const openCard = (card) => {
    if (editing) return;
    const next = {};
    card.fields.forEach((k) => { next[k] = data.member[k] == null ? '' : data.member[k]; });
    setDraft(next);
    setFieldErrors({});
    setCardError(null);
    setEditing(card.key);
  };

  const closeCard = (key) => {
    focusBackRef.current = key;
    setFieldErrors({});
    setCardError(null);
    setEditing(null);
  };

  const setDraftField = (key, value) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setFieldErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
    setCardError(null);
  };

  const saveCard = async (card) => {
    if (saving) return;
    const changed = card.fields.filter(
      (k) => data.editable_fields.includes(k) && norm(draft[k]) !== norm(data.member[k])
    );
    if (changed.length === 0) {
      closeCard(card.key);
      return;
    }
    setSaving(true);
    setFieldErrors({});
    setCardError(null);
    const body = {};
    changed.forEach((k) => { body[k] = draft[k]; });
    try {
      const payload = await updateMemberProfile(familyId, personId, body);
      setData(payload);
      closeCard(card.key);
      if (payload.saved_changes > 0) {
        setFlash(card.key);
        showSaved();
        loadHistory();
      }
    } catch (e) {
      const r = e.response;
      if (r && (r.status === 400 || r.status === 409) && r.data && r.data.fields) {
        setFieldErrors(r.data.fields);
        setCardError(r.status === 409 ? r.data.error : 'Please fix the highlighted fields.');
      } else if (r && r.status === 403) {
        setCardError(r.data?.error || 'You do not have permission to change those fields.');
      } else {
        setCardError(r?.data?.error || 'Could not save your changes. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const onPhotoPicked = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setBanner(null);
    try {
      await uploadProfilePhoto(familyId, personId, file);
      const payload = await getMemberForEdit(familyId, personId);
      setData(payload);
      loadHistory();
    } catch (err) {
      setBanner('Could not upload that photo. Please try another image.');
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    setRemoving(true);
    setRemoveError(null);
    try {
      await removeMemberFromFamily(familyId, personId);
      navigate('/family-manager', { replace: true });
    } catch (e) {
      setRemoveError(e.response?.data?.error || 'Could not remove this member. Please try again.');
      setRemoving(false);
    }
  };

  // ------------------------------------------------------------------ render
  if (loadError) {
    return (
      <HubLayout activeView="members">
        <div style={{ maxWidth: 640, margin: '60px auto', padding: 24, textAlign: 'center', fontFamily: BODY_FONT, color: C.soft }}>
          <p style={{ fontSize: 17 }}>{loadError}</p>
          <button type="button" onClick={() => navigate('/family-manager')} style={{ marginTop: 12, minHeight: 44, color: C.primary, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}>
            Back to Family Members
          </button>
        </div>
      </HubLayout>
    );
  }
  if (!data) {
    return (
      <HubLayout activeView="members">
        <div style={{ padding: 60, textAlign: 'center', color: C.muted, fontFamily: BODY_FONT }}>Loading...</div>
      </HubLayout>
    );
  }

  const m = data.member;
  const first = m.first_name || 'this member';
  const fullName = `${m.first_name} ${m.last_name}`.trim() || 'Member';
  const p = data.pronouns;
  const verbFor = (singular, plural) => (p.subject === 'they' ? plural : singular);
  const possessiveNoun = { his: 'his', her: 'hers', their: 'theirs' }[p.possessive] || 'theirs';
  const isChild = data.kind === 'child';
  const isAdult = data.kind === 'active_adult';
  const isLocked = (k) => data.locked_fields.includes(k);
  const lockedHint = `Only ${first} can change this.`;
  const coGuardians = data.guardians.filter((g) => !g.is_you).map((g) => g.name);
  const todayIso = new Date().toISOString().slice(0, 10);

  let notice;
  if (isAdult) {
    notice = `${first} has ${p.possessive} own account. You can edit ${p.possessive} family details here. ${cap(p.possessive)} email, phone and privacy settings are ${possessiveNoun} to manage.`;
  } else if (isChild) {
    notice = `You're ${first}'s guardian, so you can edit ${p.possessive} whole profile until ${p.subject} ${verbFor('turns', 'turn')} 18.`;
  } else if (data.status.key === 'tree') {
    notice = `${first} is on the family tree without an account. You can edit ${p.possessive} details here.`;
  } else {
    notice = `${first} hasn't accepted the invite yet. You can edit ${p.possessive} details until ${p.subject} ${verbFor('joins', 'join')}. After that, ${p.subject} ${verbFor('manages', 'manage')} ${p.possessive} own email and privacy.`;
  }

  let historyFooter;
  if (isAdult) historyFooter = `${first} gets an email when someone else edits ${p.possessive} profile.`;
  else if (isChild) historyFooter = `${first}'s guardian gets an email when someone else edits ${p.possessive} profile.`;
  else historyFooter = `${first} will see this history when ${p.subject} ${verbFor('joins', 'join')}.`;

  // ---- view mode ----
  const viewValue = (k) => {
    let text = m[k];
    if (k === 'date_of_birth') text = formatDob(m[k]);
    else if (k === 'role') text = ROLE_LABELS[m[k]] || m[k];
    else if (k === 'privacy_default') text = PRIVACY_LABELS[m[k]] || m[k];
    return text ? text : <span style={{ color: C.emptyText, fontWeight: 400 }}>Empty</span>;
  };

  const viewRow = (label, value, locked) => (
    <div className="efm-row" key={label}>
      <div className="efm-row-label">
        {label}
        {locked && <Lock size={13} color="#9A9C8C" aria-label="Locked" />}
      </div>
      <div className="efm-row-value">{value}</div>
    </div>
  );

  const renderView = (card) => {
    if (card.fields.every(isLocked)) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: C.lockedBg, color: '#6B705F', borderRadius: 10, padding: '14px 16px', fontSize: 15.5 }}>
          <Lock size={16} color="#9A9C8C" aria-hidden="true" /> {first} manages {p.possessive} own privacy.
        </div>
      );
    }
    return (
      <div>
        {card.fields.map((k) => viewRow(FIELD_LABELS[k], viewValue(k), isLocked(k)))}
        {card.key === 'family' && isChild && viewRow(
          'Guardians',
          data.guardians.length
            ? data.guardians.map((g) => `${g.name}${g.is_you ? ' (you)' : ''}`).join(', ')
            : <span style={{ color: C.emptyText, fontWeight: 400 }}>Empty</span>
        )}
        {card.key === 'family' && isChild && data.takes_over_at && (
          <p style={{ fontSize: 14.5, color: C.muted, margin: '6px 0 0' }}>
            {first} can take over {p.possessive} own profile at 18 ({data.takes_over_at}).
          </p>
        )}
      </div>
    );
  };

  // ---- edit mode ----
  const lockedRow = (key, label) => (
    <Field key={key} label={label} locked lockedHint={lockedHint}>
      <div style={{ minHeight: 46, boxSizing: 'border-box', display: 'flex', alignItems: 'center', padding: '8px 14px', background: C.lockedBg, color: '#6B705F', borderRadius: 10, fontSize: 16, wordBreak: 'break-word' }}>
        {m[key] || <span style={{ color: C.emptyText }}>Empty</span>}
      </div>
    </Field>
  );

  const textField = (key, label, extra = {}) => {
    if (isLocked(key)) return lockedRow(key, label);
    return (
      <Field key={key} id={`f-${key}`} label={label} optional={extra.optional} hint={extra.hint} error={fieldErrors[key]}>
        <input
          id={`f-${key}`}
          type={extra.type || 'text'}
          value={draft[key] ?? ''}
          max={extra.max}
          onChange={(e) => setDraftField(key, e.target.value)}
          style={inputStyle(fieldErrors[key])}
          autoComplete="off"
        />
      </Field>
    );
  };

  const renderForm = (card) => {
    switch (card.key) {
      case 'personal':
        return (
          <div className="efm-grid">
            {textField('first_name', 'First name')}
            {textField('last_name', 'Last name')}
            {textField('nickname', 'Nickname', { optional: true })}
            {textField('date_of_birth', 'Date of birth', {
              type: 'date',
              max: todayIso,
              hint: isChild && m.age != null ? `Age ${m.age} \u00B7 school year is calculated from this.` : undefined,
            })}
          </div>
        );
      case 'contact':
        return (
          <div className="efm-grid">
            {textField('location', 'Location')}
            {textField('email', 'Email address', {
              type: 'email',
              optional: isChild,
              hint: isChild
                ? `Used when ${p.subject} gets ${p.possessive} own account.`
                : data.resend_invite_on_email_change
                  ? 'Changing this resends the invite to the new address.'
                  : undefined,
            })}
            {textField('phone', 'Phone', { type: 'tel', optional: true })}
          </div>
        );
      case 'family':
        return (
          <>
            <div className="efm-grid">
              <Field id="f-relationship" label="Relationship to you" error={fieldErrors.relationship}>
                <select
                  id="f-relationship"
                  value={draft.relationship}
                  onChange={(e) => setDraftField('relationship', e.target.value)}
                  style={inputStyle(fieldErrors.relationship)}
                >
                  {data.relationship_options.map((o) => <option key={o} value={o}>{o}</option>)}
                  {!data.relationship_options.includes(draft.relationship) && (
                    <option value={draft.relationship}>{draft.relationship}</option>
                  )}
                </select>
              </Field>

              <Field
                label="Role in this family"
                error={fieldErrors.role}
                hint={
                  isChild
                    ? "Children can't be family admins."
                    : draft.role === 'viewer'
                      ? 'Currently a viewer. Choosing Member or Admin changes that.'
                      : 'Admins can edit members and invite people.'
                }
              >
                <div role="radiogroup" aria-label="Role in this family" style={{ display: 'flex', background: '#F1F0E8', borderRadius: 12, padding: 4, minHeight: 52, boxSizing: 'border-box' }}>
                  {[['member', 'Member'], ['admin', 'Admin']].map(([val, text]) => {
                    const active = draft.role === val;
                    const disabled = val === 'admin' && !data.can_be_admin;
                    return (
                      <button
                        key={val}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        disabled={disabled}
                        onClick={() => setDraftField('role', val)}
                        style={{
                          flex: 1, minHeight: 44, border: 'none', borderRadius: 9, fontSize: 15, fontFamily: BODY_FONT,
                          fontWeight: active ? 700 : 500, color: active ? C.primaryDark : C.soft,
                          background: active ? '#fff' : 'transparent',
                          boxShadow: active ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
                          opacity: disabled ? 0.45 : 1, cursor: disabled ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {text}
                      </button>
                    );
                  })}
                </div>
              </Field>
            </div>

            {isChild && (
              <div style={{ marginTop: 22 }}>
                <div style={{ fontSize: 14.5, fontWeight: 600, color: C.label, marginBottom: 8 }}>Guardians</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {data.guardians.length === 0 && <span style={{ fontSize: 15, color: C.muted }}>No guardian on record.</span>}
                  {data.guardians.map((g) => (
                    <span key={g.person_id} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, border: `1px solid ${C.cardBorder}`, borderRadius: 999, padding: '4px 14px 4px 4px', fontSize: 15 }}>
                      <Avatar name={g.name} photo={g.photo} size={28} fontSize={13} />
                      {g.name}{g.is_you ? ' (you)' : ''}
                    </span>
                  ))}
                </div>
                {data.takes_over_at && (
                  <p style={{ fontSize: 14.5, color: C.muted, margin: '10px 0 0' }}>
                    {first} can take over {p.possessive} own profile at 18 ({data.takes_over_at}).
                  </p>
                )}
              </div>
            )}
          </>
        );
      case 'privacy':
        return (
          <>
            <div role="radiogroup" aria-label="Default story privacy" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
              {[
                ['family', 'Family only', 'Everyone in the family can see'],
                ['private', 'Guardians only', isChild
                  ? (coGuardians.length ? `Only you and ${coGuardians.join(', ')} can see` : 'Only you can see')
                  : 'Only you can see'],
              ].map(([val, title, desc]) => {
                const active = draft.privacy_default === val;
                return (
                  <button
                    key={val}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setDraftField('privacy_default', val)}
                    style={{
                      textAlign: 'left', cursor: 'pointer', fontFamily: BODY_FONT, borderRadius: 12, padding: '14px 16px', minHeight: 44,
                      border: active ? `2px solid ${C.primary}` : `1px solid ${C.cardBorder}`,
                      background: active ? '#F4F7EC' : '#fff',
                      margin: active ? 0 : 1,
                    }}
                  >
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.ink }}>{title}</div>
                    <div style={{ fontSize: 14.5, color: C.soft, marginTop: 2 }}>{desc}</div>
                  </button>
                );
              })}
            </div>
            <p style={{ fontSize: 14, color: C.muted, margin: '12px 0 0' }}>Changing the default doesn't change existing stories.</p>
          </>
        );
      default:
        return null;
    }
  };

  const renderCard = (card) => {
    const isOpen = editing === card.key;
    const fullyLocked = card.fields.every(isLocked);
    const otherOpen = editing !== null && !isOpen;
    const sub =
      card.key === 'personal' ? `Shown to everyone in the ${data.family.name} family.`
        : card.key === 'privacy' ? `Default for new stories about ${first}.`
          : null;
    const titleId = `card-${card.key}-title`;

    return (
      <div key={card.key} style={{ position: 'relative', marginBottom: 28 }}>
        <section
          id={`card-${card.key}`}
          aria-labelledby={titleId}
          style={{
            background: '#fff', borderRadius: 16,
            border: isOpen ? `2px solid ${C.primary}` : `1px solid ${C.cardBorder}`,
            padding: isOpen ? 23 : 24,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
            <div style={{ minWidth: 0 }}>
              <h2 id={titleId} style={{ fontFamily: HEAD_FONT, fontWeight: 700, fontSize: 20, color: C.ink, margin: 0, lineHeight: '44px', minHeight: 44 }}>
                {card.title}
              </h2>
              {sub && <p style={{ fontSize: 15, color: C.muted, margin: '0' }}>{sub}</p>}
            </div>
            {!isOpen && !fullyLocked && (
              <button
                type="button"
                ref={(el) => { editBtnRefs.current[card.key] = el; }}
                onClick={() => openCard(card)}
                disabled={otherOpen}
                aria-label={`Edit ${card.title}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 44, padding: '0 16px', flexShrink: 0,
                  background: '#fff', border: `1px solid ${C.editBorder}`, borderRadius: 10,
                  fontFamily: BODY_FONT, fontWeight: 700, fontSize: 15, color: C.primaryDark,
                  opacity: otherOpen ? 0.4 : 1, cursor: otherOpen ? 'not-allowed' : 'pointer',
                }}
              >
                <Pencil size={16} aria-hidden="true" /> Edit
              </button>
            )}
          </div>

          {isOpen ? (
            <form
              noValidate
              onSubmit={(e) => { e.preventDefault(); saveCard(card); }}
            >
              {renderForm(card)}
              {cardError && (
                <p role="alert" style={{ background: C.dangerBg, border: `1px solid ${C.dangerBorder}`, color: C.danger, borderRadius: 10, padding: '10px 14px', margin: '16px 0 0', fontSize: 15 }}>
                  {cardError}
                </p>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
                <button
                  type="button"
                  onClick={() => closeCard(card.key)}
                  disabled={saving}
                  style={{ flex: 1, height: 46, borderRadius: 10, border: `1.5px solid ${C.editBorder}`, background: '#fff', color: C.primaryDark, fontFamily: BODY_FONT, fontWeight: 700, fontSize: 15.5, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.5 : 1 }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{ flex: 1, height: 46, borderRadius: 10, border: 'none', background: C.primary, color: '#fff', fontFamily: BODY_FONT, fontWeight: 700, fontSize: 15.5, cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1 }}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          ) : (
            renderView(card)
          )}
        </section>
        {flash === card.key && !isOpen && (
          <p className="efm-saved" aria-hidden="true" style={{ position: 'absolute', left: 4, bottom: -26, margin: 0, fontSize: 14, fontWeight: 600, color: C.savedText }}>
            {'\u2713'} Saved
          </p>
        )}
      </div>
    );
  };

  return (
    <HubLayout activeView="members">
      <style>{PAGE_CSS}</style>
      <div className="efm-sr-only" role="status" aria-live="polite">{liveMsg}</div>

      <div className="efm-page" style={{ fontFamily: BODY_FONT, color: C.ink, maxWidth: 1240, margin: '0 auto' }}>
        {/* Header */}
        <button
          type="button"
          onClick={() => go('/family-manager')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 44, background: 'none', border: 'none', cursor: 'pointer', fontSize: 15.5, fontWeight: 600, color: C.primary, padding: 0 }}
        >
          <ArrowLeft size={18} /> Family Members
        </button>

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 20, margin: '8px 0 20px' }}>
          <div style={{ position: 'relative' }}>
            <Avatar name={m.first_name} photo={m.photo} size={76} fontSize={32} />
            <button
              type="button"
              onClick={() => fileRef.current && fileRef.current.click()}
              disabled={uploading}
              aria-label="Change photo"
              style={{ position: 'absolute', right: -10, bottom: -10, width: 44, height: 44, background: 'none', border: 'none', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', opacity: uploading ? 0.5 : 1 }}
            >
              <span style={{ width: 32, height: 32, borderRadius: '50%', background: '#fff', border: `1px solid ${C.inputBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Camera size={16} color={C.soft} />
              </span>
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={onPhotoPicked} style={{ display: 'none' }} />
          </div>
          <div style={{ flex: '1 1 260px', minWidth: 0 }}>
            <h1 style={{ fontFamily: HEAD_FONT, fontWeight: 700, fontSize: 30, margin: 0, color: C.ink }}>Edit {fullName}</h1>
            <div style={{ fontSize: 15.5, color: C.soft, marginTop: 4, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
              <span>
                {m.relationship}
                {m.age != null ? ` \u00B7 Age ${m.age}` : ''}
              </span>
              <span aria-hidden="true">{'\u00B7'}</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: STATUS_COLORS[data.status.key] || C.muted }} />
                {data.status.label}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => go(`/life/${personId}`)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 44, padding: '0 18px', borderRadius: 999, border: `1.5px solid ${C.primary}`, background: '#fff', color: C.primary, fontWeight: 600, fontSize: 15, cursor: 'pointer' }}
          >
            <BookOpen size={17} /> View {first}'s life
          </button>
        </div>

        <div className="efm-cols">
          <div className="efm-main">
            <div style={{ display: 'flex', gap: 12, background: C.noticeBg, border: `1px solid ${C.noticeBorder}`, borderRadius: 12, padding: '14px 16px', marginBottom: 20, fontSize: 15.5, color: '#3F4536' }}>
              <Info size={20} color={C.primary} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{notice}</span>
            </div>

            {banner && (
              <div role="alert" style={{ background: C.dangerBg, border: `1px solid ${C.dangerBorder}`, color: C.danger, borderRadius: 12, padding: '12px 16px', marginBottom: 20, fontSize: 15 }}>
                {banner}
              </div>
            )}

            {CARDS.map(renderCard)}

            {/* Remove */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 14, border: '1px solid #EBD3C9', background: C.dangerBg, borderRadius: 16, padding: '18px 24px', marginBottom: 28 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: C.ink }}>Remove {first} from the family</div>
                <div style={{ fontSize: 14.5, color: C.soft }}>Their stories stay. {cap(p.subject)} {verbFor('loses', 'lose')} access to the family.</div>
              </div>
              <button
                type="button"
                onClick={() => { setRemoveError(null); setShowRemove(true); }}
                style={{ minHeight: 44, padding: '0 22px', borderRadius: 999, border: `1.5px solid ${C.dangerBorder}`, background: '#fff', color: C.danger, fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
              >
                Remove
              </button>
            </div>
          </div>

          {/* History panel */}
          <aside className="efm-aside" style={{ background: '#fff', border: `1px solid ${C.cardBorder}`, borderRadius: 16, padding: 24, marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
              <h2 style={{ fontFamily: HEAD_FONT, fontWeight: 700, fontSize: 20, margin: 0 }}>Change history</h2>
              <span style={{ fontSize: 14, color: C.muted }}>{history.total} {history.total === 1 ? 'change' : 'changes'}</span>
            </div>
            <p style={{ fontSize: 14.5, color: C.muted, margin: '4px 0 12px' }}>
              Every change to {first}'s profile, by anyone. Entries can't be edited or deleted.
            </p>
            {history.loading ? (
              <p style={{ color: C.muted, fontSize: 15 }}>Loading history...</p>
            ) : history.entries.length === 0 ? (
              <p style={{ color: C.muted, fontSize: 15, borderTop: `1px solid ${C.rowBorder}`, paddingTop: 12 }}>No changes recorded yet.</p>
            ) : (
              <ul style={{ margin: 0, padding: 0, maxHeight: '60vh', overflowY: 'auto' }}>
                {history.entries.map((e) => <HistoryEntry key={e.id} entry={e} firstName={first} />)}
              </ul>
            )}
            {history.next && (
              <button
                type="button"
                onClick={() => loadHistory(history.next)}
                style={{ marginTop: 10, minHeight: 44, background: 'none', border: 'none', color: C.primary, fontWeight: 600, fontSize: 15, cursor: 'pointer', padding: 0 }}
              >
                Show older changes
              </button>
            )}
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', borderTop: `1px solid ${C.rowBorder}`, marginTop: 14, paddingTop: 14, fontSize: 14, color: C.soft }}>
              <Bell size={16} color={C.muted} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>{historyFooter}</span>
            </div>
          </aside>
        </div>
      </div>

      {/* Remove confirmation */}
      {showRemove && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="remove-title"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, maxWidth: 440, width: '100%', fontFamily: BODY_FONT }}>
            <h3 id="remove-title" style={{ fontFamily: HEAD_FONT, fontSize: 21, fontWeight: 700, margin: 0 }}>Remove {fullName}?</h3>
            <p style={{ fontSize: 15.5, color: C.soft, margin: '10px 0 0' }}>
              {first}'s stories stay. {cap(p.subject)} will lose access to the {data.family.name} family. This is recorded in the change history.
            </p>
            {removeError && <p role="alert" style={{ color: C.danger, fontSize: 14.5, margin: '12px 0 0' }}>{removeError}</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 22 }}>
              <button type="button" onClick={() => setShowRemove(false)} disabled={removing} style={{ minHeight: 44, padding: '0 22px', borderRadius: 999, border: `1.5px solid ${C.inputBorder}`, background: '#fff', color: C.soft, fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>
                Keep {first}
              </button>
              <button type="button" onClick={remove} disabled={removing} style={{ minHeight: 44, padding: '0 22px', borderRadius: 999, border: 'none', background: C.danger, color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer', opacity: removing ? 0.6 : 1 }}>
                {removing ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </HubLayout>
  );
}
